/**
 * The reviewer data hook. For one open review item it assembles everything the
 * triage screen needs: the item itself (located within the server-ordered open
 * queue, which also yields the `i of n` position and the previous/next ids for
 * keyboard navigation), the effective taxonomy (for the category picker and the
 * proposed-new derivation) and the full document detail (`GET /v1/documents/{id}`
 * — the AI summary, the classification rationale and the OCR signals).
 *
 * Re-keys on the item id, so keyboard next/previous re-drives the load; `reload()`
 * re-fetches after a resolution changes the queue.
 */
import { useCallback, useEffect, useState } from 'react';

import type { DocumentView, EffectiveCategory, ReviewQueueItem } from '../../api/schema';

import { useReviewApi } from './reviewApi';
import { traceOf } from './useReviewQueue';

export interface ReviewerData {
  item: ReviewQueueItem;
  /** 1-based position within the open queue. */
  index: number;
  /** Total open items. */
  total: number;
  /** Ids for keyboard/pager navigation (wrapping), or null when only one item. */
  prevId: string | null;
  nextId: string | null;
  /** Full document detail, or null if it could not be loaded. */
  document: DocumentView | null;
  categories: EffectiveCategory[];
  categoryNames: string[];
  /** True when the suggested class is a proposed new category. */
  proposedNew: boolean;
}

export type ReviewerState =
  | { status: 'loading' }
  | { status: 'error'; traceId: string | null; reload: () => void }
  | { status: 'gone'; reload: () => void }
  | { status: 'ready'; data: ReviewerData; reload: () => void };

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

const MAX_ROWS = 200;

export function useReviewer(reviewItemId: string): ReviewerState {
  const api = useReviewApi();
  const [state, setState] = useState<ReviewerState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const isAborted = (): boolean => controller.signal.aborted;
    setState({ status: 'loading' });

    void (async () => {
      try {
        const [page, categories] = await Promise.all([
          api.listQueue({ sort: 'age', limit: MAX_ROWS }, controller.signal),
          api.listCategories(controller.signal),
        ]);
        if (isAborted()) return;

        const items = page.items;
        const idx = items.findIndex((it) => it.id === reviewItemId);
        const item = idx === -1 ? undefined : items[idx];
        if (item === undefined) {
          setState({ status: 'gone', reload });
          return;
        }
        const len = items.length;
        const prevId = len > 1 ? (items[(idx - 1 + len) % len]?.id ?? null) : null;
        const nextId = len > 1 ? (items[(idx + 1) % len]?.id ?? null) : null;
        const categoryNames = categories.items.map((c) => c.name);
        const suggested = item.suggested_class;
        const proposedNew =
          suggested != null &&
          suggested.trim() !== '' &&
          !categoryNames.some((n) => n.trim().toLowerCase() === suggested.trim().toLowerCase());

        let document: DocumentView | null = null;
        try {
          document = await api.getDocument(item.document_id, controller.signal);
        } catch {
          // The decision panel works from the queue item alone; a failed detail
          // fetch degrades to no OCR signals rather than blocking triage.
          document = null;
        }
        if (isAborted()) return;

        setState({
          status: 'ready',
          reload,
          data: {
            item,
            index: idx + 1,
            total: len,
            prevId,
            nextId,
            document,
            categories: categories.items,
            categoryNames,
            proposedNew,
          },
        });
      } catch (error) {
        if (isAborted() || isAbort(error)) return;
        setState({ status: 'error', traceId: traceOf(error), reload });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [api, reviewItemId, nonce, reload]);

  return state;
}
